import {
  ArrowLeft,
  Clock,
  Code2,
  Link as LinkIcon,
  Save,
  Send,
  Share2,
  Undo2,
  Users,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  MetaFunction,
} from 'react-router';
import {
  Link,
  useFetcher,
  useLoaderData,
  useNavigate,
  useSearchParams,
} from 'react-router';
import { toast } from 'sonner';
import ExportModal from '~/components/builder/ExportModal';
import FeatureToggle from '~/components/builder/FeatureToggle';
import OnboardingTour from '~/components/builder/OnboardingTour';
import PresetSelector from '~/components/builder/PresetSelector';
import DecisionPoint from '~/components/spec/DecisionPoint';
import { getSession, requireSession } from '~/lib/auth/session';
import {
  applyPreset,
  getDependents,
  getTransitiveDependencies,
} from '~/lib/builder-logic';
import type { BuilderConfig, FeatureConfig } from '~/lib/builder-types';
import {
  getSavedSpec,
  getSpecBySlug,
  upsertSavedSpec,
} from '~/lib/db/queries/specs';
import type { Feature } from '~/lib/db/schema';
import type { FeatureDefinition } from '~/lib/feature-modules/types';

export const meta: MetaFunction<typeof loader> = ({ loaderData }) => [
  {
    title: loaderData
      ? `Build: ${loaderData.spec.name} — Specly`
      : 'Builder — Specly',
  },
];

export async function loader({ params, request }: LoaderFunctionArgs) {
  const spec = await getSpecBySlug(params.slug!);
  if (!spec) throw new Response('Not Found', { status: 404 });

  const session = await getSession(request);
  const url = new URL(request.url);
  const savedSpecId = url.searchParams.get('saved');

  let savedConfig: BuilderConfig | null = null;
  if (savedSpecId && session) {
    const saved = await getSavedSpec(savedSpecId, session.user.id);
    if (saved) savedConfig = JSON.parse(saved.configJson);
  }

  return { spec, savedConfig, userId: session?.user.id ?? null };
}

export async function action({ request, params }: ActionFunctionArgs) {
  const session = await requireSession(request);
  const body = (await request.json()) as {
    name: string;
    config: BuilderConfig;
    savedSpecId?: string;
  };

  const spec = await getSpecBySlug(params.slug!);
  if (!spec) throw new Response('Not Found', { status: 404 });

  const id = await upsertSavedSpec({
    id: body.savedSpecId,
    userId: session.user.id,
    specId: spec.id,
    name: body.name,
    configJson: JSON.stringify(body.config),
  });

  return { savedSpecId: id };
}

type SpecFeatureWithDef = {
  feature: Feature & { definition: FeatureDefinition };
  isRequired: boolean;
  defaultPreset: 'lightweight' | 'standard' | 'enterprise' | null | undefined;
  definition: FeatureDefinition;
};

type MobileTab = 'features' | 'summary' | 'decisions';

function getEffortEstimate(score: number): { effort: string; team: string } {
  if (score < 3) return { effort: '1–2 weeks', team: 'solo' };
  if (score < 6) return { effort: '3–5 weeks', team: '2–3 engineers' };
  if (score < 10) return { effort: '6–10 weeks', team: '3–5 engineers' };
  return { effort: '10+ weeks', team: '5+ engineers' };
}

export default function SpecBuilder() {
  const { spec, savedConfig, userId } = useLoaderData<typeof loader>();
  const fetcher = useFetcher<typeof action>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const specFeatures = spec.specFeatures as SpecFeatureWithDef[];

  const getInitialConfig = (): BuilderConfig => {
    if (savedConfig) return savedConfig;
    const urlConfig = searchParams.get('config');
    if (urlConfig) {
      try {
        return JSON.parse(atob(urlConfig)) as BuilderConfig;
      } catch {
        // fall through
      }
    }
    return applyPreset(specFeatures, 'standard');
  };

  const draftKey = `specly:draft:${spec.slug}:${searchParams.get('saved') ?? 'new'}`;

  const getConfigFromDraft = (): BuilderConfig | null => {
    if (typeof window === 'undefined') return null;
    try {
      const raw = window.localStorage.getItem(draftKey);
      return raw ? (JSON.parse(raw) as BuilderConfig) : null;
    } catch {
      return null;
    }
  };

  const [config, setConfig] = useState<BuilderConfig>(() => {
    const fromDraft = getConfigFromDraft();
    return fromDraft ?? getInitialConfig();
  });
  const [history, setHistory] = useState<BuilderConfig[]>([]);
  const [showExport, setShowExport] = useState(false);
  const [lastDiff, setLastDiff] = useState<{
    action: 'added' | 'removed';
    featureName: string;
    tradeoffCount: number;
    depCount: number;
  } | null>(null);
  const diffTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [savedId, setSavedId] = useState<string | undefined>(
    searchParams.get('saved') ?? undefined,
  );
  const [copiedLink, setCopiedLink] = useState(false);
  const [mobileTab, setMobileTab] = useState<MobileTab>('features');
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>(
    'idle',
  );
  const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (fetcher.data?.savedSpecId) {
      setSavedId(fetcher.data.savedSpecId);
      setSaveState('saved');
      try {
        window.localStorage.removeItem(draftKey);
      } catch {}
      toast('Spec saved');
    }
  }, [fetcher.data, draftKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(() => {
      try {
        window.localStorage.setItem(draftKey, JSON.stringify(config));
      } catch {}
    }, 400);
    return () => {
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    };
  }, [config, draftKey]);

  // Debounce URL update
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const params: Record<string, string> = {
        config: btoa(JSON.stringify(config)),
      };
      if (savedId) params.saved = savedId;
      navigate(`?${new URLSearchParams(params)}`, { replace: true });
    }, 500);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [config, savedId, navigate]);

  // Undo keyboard shortcut
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        setHistory((prev) => {
          if (prev.length === 0) return prev;
          const next = [...prev];
          const last = next.pop()!;
          setConfig(last);
          return next;
        });
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const setConfigWithHistory = useCallback(
    (newConfig: BuilderConfig | ((prev: BuilderConfig) => BuilderConfig)) => {
      setConfig((prev) => {
        setHistory((h) => [...h, prev]);
        const next =
          typeof newConfig === 'function' ? newConfig(prev) : newConfig;
        return next;
      });
      setSaveState((prev) => (prev === 'saved' ? 'idle' : prev));
    },
    [],
  );

  function handleUndo() {
    setHistory((prev) => {
      if (prev.length === 0) return prev;
      const next = [...prev];
      const last = next.pop()!;
      setConfig(last);
      return next;
    });
  }

  const handlePreset = useCallback(
    (preset: 'lightweight' | 'standard' | 'enterprise') => {
      setConfigWithHistory(applyPreset(specFeatures, preset));
    },
    [specFeatures, setConfigWithHistory],
  );

  const handleToggle = useCallback(
    (slug: string, enabled: boolean) => {
      const sf = specFeatures.find((s) => s.feature.slug === slug);
      if (sf) {
        if (diffTimerRef.current) clearTimeout(diffTimerRef.current);
        setLastDiff({
          action: enabled ? 'added' : 'removed',
          featureName: sf.feature.name,
          tradeoffCount: sf.definition.tradeoffs.length,
          depCount: sf.definition.dependencies.length,
        });
        diffTimerRef.current = setTimeout(() => setLastDiff(null), 3000);
      }
      setConfigWithHistory((prev) => {
        const next = { ...prev, preset: 'custom' as const };
        if (enabled) {
          const deps = getTransitiveDependencies(slug, specFeatures);
          const newFeatures = { ...prev.features };
          newFeatures[slug] = { ...newFeatures[slug], enabled: true };
          for (const dep of deps) {
            if (newFeatures[dep]) {
              newFeatures[dep] = { ...newFeatures[dep], enabled: true };
            }
          }
          next.features = newFeatures;
        } else {
          next.features = {
            ...prev.features,
            [slug]: { ...prev.features[slug], enabled: false },
          };
        }
        return next;
      });
    },
    [specFeatures, setConfigWithHistory],
  );

  const handleOptionChange = useCallback(
    (slug: string, groupId: string, value: string | string[]) => {
      setConfigWithHistory((prev) => ({
        ...prev,
        preset: 'custom',
        features: {
          ...prev.features,
          [slug]: {
            ...prev.features[slug],
            options: { ...prev.features[slug].options, [groupId]: value },
          },
        },
      }));
    },
    [setConfigWithHistory],
  );

  function handleSave() {
    if (!userId) return;
    setSaveState('saving');
    fetcher.submit(
      { name: spec.name, config, savedSpecId: savedId },
      { method: 'post', encType: 'application/json' },
    );
  }

  function handleCopyLink() {
    navigator.clipboard.writeText(window.location.href);
    setCopiedLink(true);
    toast('Link copied');
    setTimeout(() => setCopiedLink(false), 2000);
  }

  function handleShare() {
    const enabled = Object.entries(config.features).filter(
      ([, fc]) => fc.enabled,
    ).length;
    const total = specFeatures.length;
    const disabled = total - enabled;
    const presetLabel =
      config.preset === 'custom'
        ? 'Custom build'
        : `${config.preset[0].toUpperCase()}${config.preset.slice(1)} preset`;
    const summary = `Building "${spec.name}" on Specly — ${presetLabel}, ${enabled}/${total} features${disabled > 0 ? ` (${disabled} disabled)` : ''}.\n\n${window.location.href}`;
    const nav = navigator as Navigator & {
      share?: (data: ShareData) => Promise<void>;
    };
    const copy = () => {
      void navigator.clipboard.writeText(summary);
      toast('Share summary copied');
    };
    if (typeof nav.share === 'function') {
      nav
        .share({
          title: `Specly: ${spec.name}`,
          text: summary,
          url: window.location.href,
        })
        .catch(copy);
    } else {
      copy();
    }
  }

  const enabledSlugs = Object.entries(config.features)
    .filter(([, fc]) => fc.enabled)
    .map(([s]) => s);

  const featuresWithDef = specFeatures.map((sf) => ({
    ...sf.feature,
    definition: sf.definition,
  }));

  const allDecisionPoints = specFeatures
    .filter((sf) => config.features[sf.feature.slug]?.enabled)
    .flatMap((sf) =>
      sf.definition.decisionPoints.map((dp) => ({
        ...dp,
        featureName: sf.feature.name,
        featureSlug: sf.feature.slug,
        featureDefinition: sf.definition,
      })),
    );

  const applyDecisionPoint = useCallback(
    (
      featureSlug: string,
      definition: FeatureDefinition,
      affectsOptions: string[],
      answer: 'yes' | 'no',
    ) => {
      if (affectsOptions.length === 0) return;
      setConfigWithHistory((prev) => {
        const featureConfig = prev.features[featureSlug];
        if (!featureConfig) return prev;
        const nextOptions = { ...featureConfig.options };
        for (const group of definition.optionGroups) {
          const affectedInGroup = group.options
            .filter((o) => affectsOptions.includes(o.id))
            .map((o) => o.id);
          if (affectedInGroup.length === 0) continue;
          if (group.type === 'multi') {
            const current = Array.isArray(nextOptions[group.id])
              ? (nextOptions[group.id] as string[])
              : [];
            if (answer === 'yes') {
              const set = new Set(current);
              for (const id of affectedInGroup) set.add(id);
              nextOptions[group.id] = [...set];
            } else {
              nextOptions[group.id] = current.filter(
                (id) => !affectedInGroup.includes(id),
              );
            }
          } else {
            if (answer === 'yes') {
              nextOptions[group.id] = affectedInGroup[0];
            } else if (
              typeof nextOptions[group.id] === 'string' &&
              affectedInGroup.includes(nextOptions[group.id] as string)
            ) {
              nextOptions[group.id] = '';
            }
          }
        }
        return {
          ...prev,
          preset: 'custom' as const,
          features: {
            ...prev.features,
            [featureSlug]: { ...featureConfig, options: nextOptions },
          },
        };
      });
      toast(answer === 'yes' ? 'Applied “yes” path' : 'Applied “no” path');
    },
    [setConfigWithHistory],
  );

  // Incompatibility conflicts
  const conflicts: { a: string; b: string }[] = [];
  const seen = new Set<string>();
  for (const sf of specFeatures) {
    if (!config.features[sf.feature.slug]?.enabled) continue;
    for (const incompatSlug of sf.definition.incompatibleWith) {
      if (!config.features[incompatSlug]?.enabled) continue;
      const key = [sf.feature.slug, incompatSlug].sort().join('|');
      if (!seen.has(key)) {
        seen.add(key);
        conflicts.push({ a: sf.feature.slug, b: incompatSlug });
      }
    }
  }

  // Complexity / effort score
  const complexityScore = specFeatures
    .filter((sf) => config.features[sf.feature.slug]?.enabled)
    .reduce((sum, sf) => {
      const w =
        sf.feature.complexity === 'high'
          ? 2
          : sf.feature.complexity === 'medium'
            ? 1.5
            : 1;
      return sum + w;
    }, 0);
  const { effort, team } = getEffortEstimate(complexityScore);

  const getFeatureName = (slug: string) =>
    specFeatures.find((sf) => sf.feature.slug === slug)?.feature.name ?? slug;

  // Recommended additions: commonly paired features not yet enabled
  const recommendedSlugs = useMemo(() => {
    const paired = new Set<string>();
    for (const sf of specFeatures) {
      if (!config.features[sf.feature.slug]?.enabled) continue;
      for (const slug of sf.definition.commonlyPairedWith ?? []) {
        if (!config.features[slug]?.enabled) paired.add(slug);
      }
    }
    return [...paired].filter((slug) =>
      specFeatures.some((sf) => sf.feature.slug === slug),
    );
  }, [specFeatures, config]);

  const COMPLEXITY_DAYS: Record<string, string> = {
    low: '3–5d',
    medium: '5–10d',
    high: '10–20d',
  };

  const enabledFeatureDetails = specFeatures.filter(
    (sf) => config.features[sf.feature.slug]?.enabled,
  );

  // Gap analysis: features that appear in commonlyPairedWith of ALL spec features
  // but are not enabled. Surface those appearing most often.
  const gapSlugs = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const sf of specFeatures) {
      for (const slug of sf.definition.commonlyPairedWith ?? []) {
        if (config.features[slug]?.enabled) continue;
        if (!specFeatures.some((s) => s.feature.slug === slug)) continue;
        counts[slug] = (counts[slug] ?? 0) + 1;
      }
    }
    return Object.entries(counts)
      .filter(([, count]) => count >= 2)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([slug]) => slug);
  }, [specFeatures, config]);

  const summaryPanel = (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Summary</h2>
        <div className="space-y-1 mb-4">
          {specFeatures.map((sf) => {
            const enabled = config.features[sf.feature.slug]?.enabled;
            return (
              <div
                key={sf.feature.slug}
                className={`flex items-center gap-2 text-sm ${enabled ? 'text-gray-800' : 'text-gray-400'}`}
              >
                <div
                  className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${enabled ? 'bg-indigo-500' : 'bg-gray-300'}`}
                />
                {sf.feature.name}
              </div>
            );
          })}
        </div>
        <div className="border-t pt-3">
          <p className="text-xs text-gray-500">
            {enabledSlugs.length} of {specFeatures.length} features enabled
          </p>
        </div>
      </div>

      {recommendedSlugs.length > 0 && (
        <div className="bg-amber-50 rounded-xl border border-amber-200 p-4">
          <h2 className="text-sm font-semibold text-amber-800 mb-2">
            Commonly added together
          </h2>
          <div className="space-y-1.5">
            {recommendedSlugs.map((slug) => {
              const sf = specFeatures.find((s) => s.feature.slug === slug);
              if (!sf) return null;
              return (
                <button
                  key={slug}
                  type="button"
                  onClick={() => handleToggle(slug, true)}
                  className="w-full flex items-center justify-between text-left px-2.5 py-1.5 bg-white rounded-lg border border-amber-200 hover:border-amber-400 transition-colors group"
                >
                  <span className="text-sm text-gray-700">
                    {sf.feature.name}
                  </span>
                  <span className="text-xs text-amber-600 group-hover:text-amber-800">
                    + Add
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {gapSlugs.length > 0 && (
        <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
          <h2 className="text-sm font-semibold text-blue-800 mb-2">
            Gap analysis
          </h2>
          <div className="space-y-1.5">
            {gapSlugs.map((slug) => {
              const sf = specFeatures.find((s) => s.feature.slug === slug);
              if (!sf) return null;
              return (
                <div
                  key={slug}
                  className="flex items-start justify-between gap-2"
                >
                  <p className="text-xs text-blue-700 leading-relaxed">
                    Most {spec.category} apps include{' '}
                    <strong>{sf.feature.name}</strong>
                  </p>
                  <button
                    type="button"
                    onClick={() => handleToggle(slug, true)}
                    className="flex-shrink-0 text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    + Add
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-2">
          <Clock className="h-4 w-4 text-gray-400" />
          <h2 className="text-sm font-semibold text-gray-700">
            Effort Estimate
          </h2>
        </div>
        <p className="text-sm text-gray-800 font-medium">{effort}</p>
        <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-1">
          <Users className="h-3.5 w-3.5" />
          {team}
        </div>
        <p className="text-xs text-gray-400 mt-2">
          {enabledSlugs.length} enabled features
        </p>
        {enabledFeatureDetails.length > 0 && (
          <button
            type="button"
            onClick={() => setShowBreakdown((v) => !v)}
            className="mt-2 text-xs text-indigo-600 hover:text-indigo-800 font-medium"
          >
            {showBreakdown ? 'Hide breakdown' : 'Show breakdown'}
          </button>
        )}
        {showBreakdown && enabledFeatureDetails.length > 0 && (
          <div className="mt-3 border-t pt-3">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-gray-400">
                  <th className="text-left font-medium pb-1.5">Feature</th>
                  <th className="text-left font-medium pb-1.5">Complexity</th>
                  <th className="text-right font-medium pb-1.5">Est.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {enabledFeatureDetails.map((sf) => (
                  <tr key={sf.feature.slug}>
                    <td className="py-1 text-gray-700 pr-2">
                      {sf.feature.name}
                    </td>
                    <td className="py-1 capitalize text-gray-500">
                      {sf.feature.complexity}
                    </td>
                    <td className="py-1 text-right text-gray-500">
                      {COMPLEXITY_DAYS[sf.feature.complexity] ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const decisionsPanel =
    allDecisionPoints.length > 0 ? (
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">
          Key Decisions
        </h2>
        <div className="space-y-3">
          {allDecisionPoints.map((dp, i) => (
            <div key={i}>
              <p className="text-xs text-gray-400 mb-1">{dp.featureName}</p>
              <DecisionPoint point={dp} />
              {dp.affectsOptions && dp.affectsOptions.length > 0 && (
                <div className="mt-2 flex items-center gap-2 text-xs">
                  <span className="text-gray-400">Apply:</span>
                  <button
                    type="button"
                    onClick={() =>
                      applyDecisionPoint(
                        dp.featureSlug,
                        dp.featureDefinition,
                        dp.affectsOptions,
                        'yes',
                      )
                    }
                    className="px-2 py-0.5 rounded border border-emerald-200 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 font-medium"
                  >
                    Yes path
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      applyDecisionPoint(
                        dp.featureSlug,
                        dp.featureDefinition,
                        dp.affectsOptions,
                        'no',
                      )
                    }
                    className="px-2 py-0.5 rounded border border-gray-200 text-gray-600 bg-gray-50 hover:bg-gray-100 font-medium"
                  >
                    No path
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    ) : null;

  const featuresPanel = (
    <div className="space-y-2">
      {specFeatures.map((sf) => {
        const featureConfig: FeatureConfig = config.features[
          sf.feature.slug
        ] ?? {
          enabled: false,
          options: {},
        };
        const dependents = !featureConfig.enabled
          ? []
          : getDependents(sf.feature.slug, specFeatures, enabledSlugs);
        const warning =
          !featureConfig.enabled && dependents.length > 0
            ? `Required by: ${dependents.join(', ')}`
            : undefined;

        return (
          <FeatureToggle
            key={sf.feature.slug}
            feature={{ ...sf.feature, definition: sf.definition }}
            config={featureConfig}
            isRequired={sf.isRequired}
            dependentWarning={warning}
            onToggle={handleToggle}
            onOptionChange={handleOptionChange}
          />
        );
      })}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24 lg:pb-8">
      <div className="mb-6">
        <Link
          to={`/specs/${spec.slug}`}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-3"
        >
          <ArrowLeft className="h-4 w-4" />
          {spec.name}
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Build: {spec.name}
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Toggle features and choose options to customize your spec
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleUndo}
              disabled={history.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Undo2 className="h-4 w-4" />
              Undo
            </button>
            <button
              type="button"
              onClick={handleCopyLink}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              <LinkIcon className="h-4 w-4" />
              {copiedLink ? 'Copied!' : 'Copy link'}
            </button>
            <button
              type="button"
              onClick={handleShare}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              <Send className="h-4 w-4" />
              Share
            </button>
            {userId ? (
              <button
                type="button"
                onClick={handleSave}
                disabled={saveState === 'saving'}
                className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {saveState === 'saving'
                  ? 'Saving…'
                  : saveState === 'saved' || savedId
                    ? 'Saved'
                    : 'Save'}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setShowExport(true)}
              className="flex items-center gap-2 px-4 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              <Share2 className="h-4 w-4" />
              Export
            </button>
            <Link
              to={`/specs/${spec.slug}/tech?config=${btoa(JSON.stringify(config))}`}
              className="flex items-center gap-2 px-4 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
            >
              <Code2 className="h-4 w-4" />
              Technical Spec
            </Link>
          </div>
        </div>

        {!userId && (
          <div className="mt-3 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3 text-sm">
            <span className="text-amber-800">Sign in to save your spec</span>
            <Link
              to="/auth/login"
              className="text-indigo-600 hover:text-indigo-800 font-medium underline"
            >
              Sign in
            </Link>
          </div>
        )}
      </div>

      {conflicts.length > 0 && (
        <div className="mb-4 space-y-2">
          {conflicts.map((c, i) => (
            <div
              key={i}
              className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800"
            >
              ⚠ <strong>{getFeatureName(c.a)}</strong> and{' '}
              <strong>{getFeatureName(c.b)}</strong> are incompatible — choose
              one.
            </div>
          ))}
        </div>
      )}

      {/* Desktop layout */}
      <div className="hidden lg:grid grid-cols-3 gap-6">
        <div className="col-span-2 space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">Preset</h2>
            <PresetSelector
              value={config.preset}
              onChange={handlePreset}
              specFeatures={specFeatures}
            />
          </div>
          {featuresPanel}
        </div>
        <div className="space-y-4 sticky top-20 self-start">
          {summaryPanel}
          {decisionsPanel}
        </div>
      </div>

      {/* Mobile layout */}
      <div className="lg:hidden">
        {mobileTab === 'features' && (
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h2 className="text-sm font-semibold text-gray-700 mb-3">
                Preset
              </h2>
              <PresetSelector
                value={config.preset}
                onChange={handlePreset}
                specFeatures={specFeatures}
              />
            </div>
            {featuresPanel}
          </div>
        )}
        {mobileTab === 'summary' && summaryPanel}
        {mobileTab === 'decisions' &&
          (decisionsPanel ?? (
            <div className="text-center py-12 text-gray-400 text-sm">
              Enable features to see key decisions
            </div>
          ))}
      </div>

      {/* Mobile tab bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex">
        {(['features', 'summary', 'decisions'] as MobileTab[]).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setMobileTab(tab)}
            className={`flex-1 py-3 text-sm font-medium capitalize transition-colors ${
              mobileTab === tab
                ? 'text-indigo-600 border-t-2 border-indigo-600 -mt-px'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      <OnboardingTour />

      {showExport && (
        <ExportModal
          spec={spec}
          config={config}
          features={featuresWithDef as any}
          onClose={() => setShowExport(false)}
          onImport={(imported) => setConfigWithHistory(imported)}
        />
      )}

      {/* Live diff toast */}
      {lastDiff && (
        <div className="fixed bottom-20 lg:bottom-6 left-1/2 -translate-x-1/2 z-40 animate-in slide-in-from-bottom-4 fade-in">
          <div
            className={`flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border text-sm font-medium ${
              lastDiff.action === 'added'
                ? 'bg-green-50 border-green-200 text-green-800'
                : 'bg-gray-50 border-gray-200 text-gray-700'
            }`}
          >
            <span
              className={`text-base ${lastDiff.action === 'added' ? 'text-green-500' : 'text-gray-400'}`}
            >
              {lastDiff.action === 'added' ? '＋' : '－'}
            </span>
            <span>
              {lastDiff.action === 'added' ? 'Added' : 'Removed'}{' '}
              <strong>{lastDiff.featureName}</strong>
              {lastDiff.action === 'added' && lastDiff.tradeoffCount > 0 && (
                <span className="font-normal text-green-600">
                  {' '}
                  · +{lastDiff.tradeoffCount} tradeoffs
                </span>
              )}
              {lastDiff.action === 'removed' && lastDiff.depCount > 0 && (
                <span className="font-normal text-gray-500">
                  {' '}
                  · freed {lastDiff.depCount} dependencies
                </span>
              )}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
